<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import type { HomePageConfig } from '../configs/types'
import type { DramaInfo, VideoBridgeListItem } from '../configs/drama'
import { mapVideoBridgeItem } from '../configs/drama'
import DramaCharacterPopup from './DramaCharacterPopup.vue'
import { getDramaPlayerUrl } from '@/constants'
import { colors } from '@/config/brands'
import { useElementClick } from '@/composables/tracks'
import { api } from '@/api'

const props = defineProps<{
  config: HomePageConfig
  /** Drama 页签是否处于激活状态，驱动视频播放/暂停 */
  panelActive: boolean
}>()

const { elementClick } = useElementClick()

// 一期只有一个剧，取桥接列表第一条
const drama = ref<DramaInfo | null>(null)
const fetching = ref(false)
const error = ref(false)

async function fetchDrama() {
  fetching.value = true
  error.value = false
  try {
    const { data } = await api.get<any, { data: VideoBridgeListItem[] }>('/interactive/book/video-bridge/list')
    drama.value = mapVideoBridgeItem(data?.[0])
  }
  catch {
    error.value = true
  }
  fetching.value = false
}

onMounted(() => {
  fetchDrama()
})

const ctaText = computed(() => {
  const texts = props.config.texts
  if (!drama.value)
    return texts.play
  switch (drama.value.watchStatus) {
    case 'in_progress':
      return texts.continueEp(drama.value.currentEpisode)
    case 'completed':
      return texts.playAgain
    default:
      return texts.play
  }
})

const statusText = computed(() => {
  const texts = props.config.texts
  if (!drama.value)
    return texts.makeTheChoice
  switch (drama.value.watchStatus) {
    case 'completed':
      return texts.endingsUnlocked(drama.value.unlockedEndings)
    // in_progress 按设计稿仍展示 Make the choice（进度百分比后端暂未下发）
    default:
      return texts.makeTheChoice
  }
})

function goDramaPlayer(from: string) {
  if (!drama.value)
    return
  elementClick(`drama_${from}`)
  // 有进度也只落播放器 home 页（章节封面页），仅 Library 的 Continue 直达游戏
  window.location.href = getDramaPlayerUrl(drama.value.id, 'drama')
}

// 人物弹窗
const characterPopupVisible = ref(false)
const characterIndex = ref(0)

function openCharacter(index: number) {
  elementClick('drama_character')
  characterIndex.value = index
  characterPopupVisible.value = true
}

// 人物列表横向滚动进度（0 ~ 1），驱动底部进度条
const characterScrollRatio = ref(0)

function onCharacterScroll(event: Event) {
  const el = event.target as HTMLElement
  const max = el.scrollWidth - el.clientWidth
  characterScrollRatio.value = max > 0 ? Math.min(Math.max(el.scrollLeft / max, 0), 1) : 0
}
</script>

<template>
  <div class="drama-panel h-full overflow-y-scroll">
    <div v-if="error" @click="fetchDrama">
      <BizBlockError />
    </div>
    <div v-else-if="fetching && !drama" class="h-full flex items-center justify-center">
      <BasicSpinner />
    </div>
    <BizBlockEmpty v-else-if="!drama" />
    <div v-else class="pb-90px">
      <!-- 顶部视频封面 -->
      <div class="drama-video mx-16px mt-12px" @click="goDramaPlayer('cover')">
        <BizDramaVideo
          :src="drama.videoUrl"
          :poster="drama.coverUrl"
          :active="panelActive"
        >
          <template #cover>
            <img src="/images/vowpath/home/icon-play-circle.png" class="play-circle">
          </template>
        </BizDramaVideo>
      </div>

      <!-- 标题 / 状态 / CTA -->
      <h1 class="drama-title mt-24px px-16px">
        {{ drama.title }}
      </h1>
      <p class="drama-subtitle mt-8px px-16px">
        {{ statusText }}
      </p>
      <div class="flex justify-center mt-16px">
        <button class="drama-cta" @click="goDramaPlayer('cta')">
          <i class="play-triangle" />
          <span>{{ ctaText }}</span>
        </button>
      </div>

      <i class="drama-divider mx-16px mt-24px" />

      <!-- 标签 -->
      <div class="flex flex-wrap gap-10px px-16px mt-20px">
        <div
          v-for="tag in drama.tags"
          :key="tag.name"
          class="drama-tag"
          :class="{ highlight: tag.highlight }"
        >
          <img v-if="tag.hot" src="/images/vowpath/home/icon-hot-white.png" class="tag-hot">
          <span>{{ tag.name }}</span>
        </div>
      </div>

      <!-- Story Line -->
      <h2 class="drama-section-title mt-24px px-16px">
        {{ config.texts.storyLine }}
      </h2>
      <div class="mt-8px px-16px">
        <BasicExpandableText
          :text="drama.storyLine"
          :line-clamp="3"
          text-class="text-13px lh-20px text-white/60"
          :more-text="config.texts.more"
          more-class="text-#FF5ACE cursor-pointer text-13px lh-20px"
          :more-bg-color="colors.bg.global"
        />
      </div>

      <!-- Character Profile -->
      <template v-if="drama.characters.length">
        <h2 class="drama-section-title mt-24px px-16px">
          {{ config.texts.characterProfile }}
        </h2>
        <div class="character-scroll flex gap-13px px-16px mt-16px overflow-x-auto" @scroll="onCharacterScroll">
          <div
            v-for="(character, index) in drama.characters"
            :key="index"
            class="character-card shrink-0"
            @click="openCharacter(index)"
          >
            <img :src="character.avatar" class="character-avatar">
            <div class="character-info">
              <p class="character-name truncate">
                {{ character.name }}
              </p>
              <p v-if="character.subtitle" class="character-subtitle truncate">
                {{ character.subtitle }}
              </p>
            </div>
          </div>
        </div>
        <div class="character-progress">
          <i class="character-progress-thumb" :style="{ transform: `translateX(${characterScrollRatio * 187.5}%)` }" />
        </div>
        <DramaCharacterPopup
          v-model="characterPopupVisible"
          :config="config"
          :characters="drama.characters"
          :index="characterIndex"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.drama-panel {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.drama-video {
  aspect-ratio: 343 / 228;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
}

.play-circle {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 35px;
  height: 35px;
  z-index: 2;
}

.drama-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-style: italic;
  font-weight: bold;
  font-size: 24px;
  line-height: 32px;
  color: #ffffff;
  text-align: center;
}

.drama-subtitle {
  font-size: 15px;
  line-height: 20px;
  color: rgba(255, 255, 255, 0.7);
  text-align: center;
}

.drama-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 113px;
  height: 34px;
  padding: 0 20px;
  border: none;
  border-radius: 17px;
  background: #ffffff;
  color: #17183b;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;

  .play-triangle {
    width: 0;
    height: 0;
    margin-right: 6px;
    border-style: solid;
    border-width: 6px 0 6px 10px;
    border-color: transparent transparent transparent #17183b;
  }
}

.drama-divider {
  display: block;
  height: 2px;
  border-radius: 3px;
  background: rgba(134, 124, 123, 0.16);
}

.drama-tag {
  display: flex;
  align-items: center;
  height: 26px;
  padding: 0 12px;
  border-radius: 14px;
  background: linear-gradient(270deg, rgba(255, 90, 205, 0.2) 0%, rgba(154, 87, 255, 0.2) 100%);
  font-size: 12px;
  color: #ffffff;

  &.highlight {
    background: linear-gradient(270deg, rgba(255, 90, 205, 1) 0%, rgba(154, 87, 255, 1) 100%);
  }

  .tag-hot {
    width: 12px;
    height: 14px;
    margin-right: 4px;
  }
}

.drama-section-title {
  font-size: 18px;
  font-weight: bold;
  line-height: 24px;
  color: #ffffff;
}

.character-scroll {
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.character-card {
  position: relative;
  width: 133px;
  height: 177px;
  border-radius: 17px;
  overflow: hidden;
  background: #000000;
  cursor: pointer;

  // 设计稿 4.5px 内描边
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border: 4.5px solid rgba(255, 255, 255, 0.28);
    border-radius: 17px;
    pointer-events: none;
  }
}

.character-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.character-info {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 40px 17px 16px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
}

.character-name {
  font-family: 'Playfair Display', Georgia, 'Times New Roman', serif;
  font-size: 16px;
  line-height: 22px;
  color: #ffffff;
}

.character-subtitle {
  margin-top: 2px;
  font-size: 10px;
  line-height: 14px;
  color: rgba(255, 255, 255, 0.85);
}

.character-progress {
  width: 46px;
  height: 4px;
  margin: 17px auto 0;
  border-radius: 3px;
  overflow: hidden;
  background: linear-gradient(270deg, rgba(61, 59, 101, 1) 0%, rgba(161, 98, 255, 1) 100%);
}

.character-progress-thumb {
  display: block;
  width: 16px;
  height: 4px;
  border-radius: 3px;
  background: linear-gradient(270deg, rgba(255, 107, 210, 1) 0%, rgba(161, 98, 255, 1) 100%);
}
</style>
